(() => {
  const modal = document.querySelector("[data-gd-quick-view-modal]");
  const content = modal && modal.querySelector("[data-gd-quick-view-content]");

  if (!modal || !content) {
    document.addEventListener("click", event => {
      const trigger = event.target.closest("[data-gd-quick-view-trigger]");
      if (!trigger) return;
      const handle = trigger.getAttribute("data-product-handle");
      if (!handle) return;
      window.location.href = `/products/${handle}`;
    });
    return;
  }

  const root = content.querySelector("[data-gd-qv-root]");
  const loadingNode = content.querySelector("[data-gd-qv-loading]");
  const mainStage = content.querySelector("[data-gd-qv-main-stage]");
  const mainImageNode = content.querySelector("[data-gd-qv-main-image]");
  const thumbsNode = content.querySelector("[data-gd-qv-thumbs]");
  const titleNode = content.querySelector("[data-gd-qv-title]");
  const badgesNode = content.querySelector("[data-gd-qv-badges]");
  const descriptionNode = content.querySelector("[data-gd-qv-description]");
  const addToCartNode = content.querySelector("[data-gd-qv-add-to-cart]");
  const addToCartLabelNode = content.querySelector("[data-gd-qv-add-label]");
  const addToCartPriceNode = content.querySelector("[data-gd-qv-add-price]");
  const productLinkNode = content.querySelector("[data-gd-qv-product-link]");

  const mainStageEl = mainStage || (mainImageNode && mainImageNode.parentElement);

  if (
    !root ||
    !loadingNode ||
    !mainImageNode ||
    !mainStageEl ||
    !thumbsNode ||
    !titleNode ||
    !badgesNode ||
    !descriptionNode ||
    !addToCartNode ||
    !addToCartLabelNode ||
    !addToCartPriceNode ||
    !productLinkNode
  ) return;

  const state = {
    product: null,
    variant: null,
    badgesHtml: "",
    cardMediaItems: null,
    mediaItems: []
  };

  /**
   * Product JSON often returns //cdn.shopify.com/s/files/{id}/{id}/files/...
   * Storefront serves the same assets as /cdn/shop/files/... on the shop host,
   * which avoids intermittent load issues with the generic CDN URL.
   */
  const normalizeProductImageUrl = url => {
    if (!url || typeof url !== "string") return url;
    const origin = window.location.origin;
    let absolute = url;
    if (url.startsWith("//")) {
      absolute = `${window.location.protocol}${url}`;
    }
    try {
      const parsed = new URL(absolute);
      if (parsed.hostname === window.location.hostname && parsed.pathname.includes("/cdn/shop/")) {
        return absolute;
      }
      const match = parsed.pathname.match(/^\/s\/files\/(?:\d+\/)+files\/(.+)$/);
      if (match && /cdn\.shopify\.com$/i.test(parsed.hostname)) {
        const filePath = match[1];
        return `${origin}/cdn/shop/files/${filePath}${parsed.search}`;
      }
    } catch (_) {}
    return absolute;
  };

  const resolveMediaUrl = url => {
    if (!url || typeof url !== "string") return url;
    if (url.startsWith("//")) return `${window.location.protocol}${url}`;
    if (url.startsWith("/")) return `${window.location.origin}${url}`;
    return normalizeProductImageUrl(url);
  };

  const pickVideoSourceUrl = s => {
    if (!s || typeof s !== "object") return "";
    const u = s.url || s.src || s.URL || s.playback_url;
    return typeof u === "string" && u.trim() ? u.trim() : "";
  };

  const normalizeMediaItem = item => {
    if (!item || typeof item !== "object") return item;
    const out = { ...item };
    if (out.src) out.src = normalizeProductImageUrl(out.src);
    if (out.preview_src) out.preview_src = normalizeProductImageUrl(out.preview_src);
    if (out.playback_url) out.playback_url = resolveMediaUrl(out.playback_url);
    if (Array.isArray(out.sources)) {
      out.sources = out.sources.map(s => {
        const raw = pickVideoSourceUrl(s);
        if (!raw) return s;
        return { ...s, url: resolveMediaUrl(raw) };
      });
    }
    return out;
  };

  const externalVideoEmbedSrc = item => {
    const host = String(item.host || "").toLowerCase();
    const id = item.external_id;
    if (id === undefined || id === null || id === "") return null;
    const enc = encodeURIComponent(String(id));
    if (host.includes("youtube")) return `https://www.youtube.com/embed/${enc}`;
    if (host.includes("vimeo")) return `https://player.vimeo.com/video/${enc}`;
    return null;
  };

  const clearMainStageMedia = () => {
    mainStageEl.querySelectorAll("video, iframe").forEach(el => el.remove());
    mainImageNode.classList.remove("is-hidden");
  };

  const showMainMediaItem = item => {
    clearMainStageMedia();
    if (!item) return;

    if (item.media_type === "image" || item.media_type === "model") {
      if (item.src) mainImageNode.src = item.src;
      return;
    }

    if (item.media_type === "video") {
      const sources = Array.isArray(item.sources) ? item.sources : [];
      const fallbackUrl = item.playback_url || pickVideoSourceUrl(sources[0]);
      const hasAnyUrl =
        Boolean(fallbackUrl) || sources.some(s => Boolean(pickVideoSourceUrl(s)));
      if (!hasAnyUrl) return;

      mainImageNode.classList.add("is-hidden");
      const video = document.createElement("video");
      video.className = "gd_qv__main_video";
      video.controls = true;
      video.playsInline = true;
      video.setAttribute("controls", "");
      video.setAttribute("playsinline", "");
      video.preload = "metadata";
      const posterRaw = item.preview_src;
      if (posterRaw) {
        video.setAttribute("poster", resolveMediaUrl(posterRaw));
      }
      let added = false;
      sources.forEach(s => {
        const u = pickVideoSourceUrl(s);
        if (!u) return;
        const source = document.createElement("source");
        source.src = resolveMediaUrl(u);
        const type = s.mime_type || s.type;
        if (type) source.type = type;
        video.appendChild(source);
        added = true;
      });
      if (!added && fallbackUrl) {
        video.src = resolveMediaUrl(fallbackUrl);
      }
      mainStageEl.appendChild(video);
      return;
    }

    if (item.media_type === "external_video") {
      const src = externalVideoEmbedSrc(item);
      if (!src) return;
      mainImageNode.classList.add("is-hidden");
      const iframe = document.createElement("iframe");
      iframe.className = "gd_qv__main_iframe";
      iframe.setAttribute("src", src);
      iframe.setAttribute("title", "Product video");
      iframe.setAttribute("allowfullscreen", "");
      iframe.setAttribute(
        "allow",
        "accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
      );
      mainStageEl.appendChild(iframe);
    }
  };

  const money = cents => {
    const amountInCents = Number(cents || 0);
    const moneyFormat = window.theme && window.theme.moneyFormat
      ? window.theme.moneyFormat
      : window.Shopify && window.Shopify.money_format
        ? window.Shopify.money_format
        : null;
    if (window.Shopify && typeof window.Shopify.formatMoney === "function" && moneyFormat) {
      try {
        return window.Shopify.formatMoney(amountInCents, moneyFormat);
      } catch (_) {}
    }
    const amount = amountInCents / 100;
    try {
      return new Intl.NumberFormat("en-US", {
        style: "currency",
        currency: "USD"
      }).format(amount);
    } catch (_) {
      return `$${amount.toFixed(2)}`;
    }
  };

  const setLoadingState = visible => {
    root.hidden = visible;
    loadingNode.hidden = !visible;
  };

  const sanitizeHtml = (html = "") => {
    const template = document.createElement("template");
    template.innerHTML = html;

    template.content.querySelectorAll("script, style, iframe, object, embed").forEach(node => {
      node.remove();
    });

    template.content.querySelectorAll("*").forEach(element => {
      [...element.attributes].forEach(attribute => {
        const attrName = attribute.name.toLowerCase();
        const attrValue = attribute.value.toLowerCase();
        if (attrName.startsWith("on")) {
          element.removeAttribute(attribute.name);
          return;
        }
        if ((attrName === "href" || attrName === "src") && attrValue.startsWith("javascript:")) {
          element.removeAttribute(attribute.name);
        }
      });
    });

    return template.innerHTML;
  };

  const resetUi = () => {
    clearMainStageMedia();
    mainImageNode.src = "";
    mainImageNode.alt = "";
    titleNode.textContent = "";
    badgesNode.innerHTML = "";
    descriptionNode.innerHTML = "";
    thumbsNode.innerHTML = "";
    productLinkNode.setAttribute("href", "#");
    addToCartNode.disabled = true;
    addToCartLabelNode.textContent = "Add to Cart";
    addToCartPriceNode.textContent = "";
    state.mediaItems = [];
    state.cardMediaItems = null;
  };

  const thumbSrcForItem = item => {
    if (!item) return "";
    if (item.media_type === "image" || item.media_type === "model") return item.src || "";
    return item.preview_src || "";
  };

  const renderMediaThumbs = (items, productTitle) => {
    thumbsNode.innerHTML = "";
    items.forEach((rawItem, index) => {
      const item = normalizeMediaItem(rawItem);
      const thumbSrc = thumbSrcForItem(item);
      const thumbButton = document.createElement("button");
      thumbButton.type = "button";
      thumbButton.className = "gd_qv__thumb";
      thumbButton.setAttribute("data-gd-qv-thumb-index", String(index));

      const thumbImage = document.createElement("img");
      thumbImage.src = thumbSrc || "";
      thumbImage.alt = productTitle;
      thumbButton.appendChild(thumbImage);

      thumbsNode.appendChild(thumbButton);
    });
  };

  const closeQuickView = () => {
    modal.hidden = true;
    modal.classList.remove("is-loading");
    document.body.classList.remove("gd-qv-open");
    setLoadingState(false);
    resetUi();
  };

  const openQuickView = () => {
    modal.hidden = false;
    document.body.classList.add("gd-qv-open");
  };

  const updateVariantUi = variant => {
    if (!variant) return;
    state.variant = variant;
    addToCartNode.disabled = !variant.available;
    addToCartLabelNode.textContent = variant.available ? "Add to Cart" : "Sold Out";
    addToCartPriceNode.textContent = money(variant.price);

    const variantImageRaw = variant.featured_image && variant.featured_image.src ? variant.featured_image.src : null;
    const variantImage = variantImageRaw ? normalizeProductImageUrl(variantImageRaw) : null;
    if (variantImage) {
      clearMainStageMedia();
      mainImageNode.src = variantImage;
    }
  };

  const renderQuickView = () => {
    if (!state.product || !state.variant) return;

    const { product, variant } = state;

    let items = state.cardMediaItems;
    if (!Array.isArray(items) || !items.length) {
      items = (product.images || []).map(src => ({
        media_type: "image",
        src: normalizeProductImageUrl(src)
      }));
    } else {
      items = items.map(normalizeMediaItem);
    }

    state.mediaItems = items;

    const mainImageRaw = variant.featured_image && variant.featured_image.src
      ? variant.featured_image.src
      : null;
    const variantHasImage = Boolean(mainImageRaw);

    setLoadingState(false);
    titleNode.textContent = product.title ? `MEET ${String(product.title).toUpperCase()}` : "";
    badgesNode.innerHTML = state.badgesHtml || "";
    mainImageNode.alt = product.title || "";
    descriptionNode.innerHTML = sanitizeHtml(product.description || "");
    productLinkNode.setAttribute("href", product.url || "#");

    if (items.length) {
      renderMediaThumbs(items, product.title || "Product image");
      if (!variantHasImage) {
        showMainMediaItem(items[0]);
      } else {
        clearMainStageMedia();
        mainImageNode.src = normalizeProductImageUrl(mainImageRaw);
      }
    } else {
      mainImageNode.src = "";
    }

    updateVariantUi(variant);
  };

  const parseCardMedia = trigger => {
    if (!trigger) return null;
    const raw = trigger.getAttribute("data-gd-qv-media");
    if (!raw || !raw.trim()) return null;
    try {
      const parsed = JSON.parse(raw);
      return Array.isArray(parsed) ? parsed : null;
    } catch (_) {
      return null;
    }
  };

  const openProductQuickView = (handle, trigger) => {
    state.cardMediaItems = parseCardMedia(trigger);

    loadingNode.textContent = "";
    setLoadingState(true);
    openQuickView();
    modal.classList.add("is-loading");

    const abortController = new AbortController();
    const requestTimeout = window.setTimeout(() => {
      abortController.abort();
    }, 8000);

    fetch(`/products/${handle}.js`, { signal: abortController.signal })
      .then(response => {
        if (!response.ok) throw new Error("Failed to load product");
        return response.json();
      })
      .then(product => {
        state.product = product;
        state.variant = product.selected_or_first_available_variant || product.variants[0];
        renderQuickView();
        modal.classList.remove("is-loading");
        window.clearTimeout(requestTimeout);
      })
      .catch(() => {
        window.clearTimeout(requestTimeout);
        modal.classList.remove("is-loading");
        setLoadingState(false);
      });
  };

  document.addEventListener("click", event => {
    const trigger = event.target.closest("[data-gd-quick-view-trigger]");
    if (trigger) {
      event.preventDefault();
      event.stopPropagation();

      const card = trigger.closest(".gd_card");
      const badges = card && card.querySelector(".guide-dog-badges");
      state.badgesHtml = badges ? badges.outerHTML : "";

      const handle = trigger.getAttribute("data-product-handle");
      if (handle) openProductQuickView(handle, trigger);
      return;
    }

    if (event.target.closest("[data-gd-quick-view-close]")) {
      event.preventDefault();
      closeQuickView();
      return;
    }

    const thumb = event.target.closest(".gd_qv__thumb");
    if (thumb) {
      const idx = thumb.getAttribute("data-gd-qv-thumb-index");
      if (idx === null || !state.mediaItems.length) return;
      const item = state.mediaItems[Number(idx)];
      if (item) showMainMediaItem(item);
      return;
    }

    const addToCart = event.target.closest("[data-gd-qv-add-to-cart]");
    if (addToCart) {
      event.preventDefault();
      if (!state.variant || !state.variant.available) return;

      addToCartNode.disabled = true;

      fetch("/cart/add.js", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json"
        },
        body: JSON.stringify({
          id: state.variant.id,
          quantity: 1
        })
      })
        .then(response => {
          if (!response.ok) throw new Error("Add to cart failed");
          return response.json();
        })
        .then(() => {
        })
        .catch(() => {})
        .finally(() => {
          addToCartNode.disabled = !state.variant.available;
        });
    }
  }, true);

  document.addEventListener("keydown", event => {
    if (event.key === "Escape" && !modal.hidden) {
      closeQuickView();
    }
  });
})();
